import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useWizardState } from "../hooks/use-wizard.js";

const POLL_INTERVAL_MS = 2000;
const TIMEOUT_MS = 90_000;

export function BillingSuccessPage() {
  const [params] = useSearchParams();
  const websiteId = params.get("websiteId");
  const navigate = useNavigate();
  const { data: wizardState, refetch } = useWizardState(websiteId ?? undefined);
  const startTime = useRef(Date.now());
  const [timedOut, setTimedOut] = useState(false);

  // If no websiteId in query params, redirect to app root after a brief delay
  useEffect(() => {
    if (!websiteId) {
      const t = setTimeout(() => void navigate("/app", { replace: true }), 2000);
      return () => clearTimeout(t);
    }
  }, [websiteId, navigate]);

  // Reactive timeout — forces re-render when time is up
  useEffect(() => {
    const remaining = TIMEOUT_MS - (Date.now() - startTime.current);
    if (remaining <= 0) {
      setTimedOut(true);
      return;
    }
    const handle = setTimeout(() => setTimedOut(true), remaining);
    return () => clearTimeout(handle);
  }, []);

  useEffect(() => {
    if (!wizardState || !websiteId) return;

    // If plan_selection is done, proceed to wizard
    if (wizardState.currentStep !== "plan_selection") {
      navigate(`/wizard/${websiteId}`, { replace: true });
      return;
    }
  }, [wizardState, websiteId, navigate]);

  // Re-poll every 2 seconds
  useEffect(() => {
    const id = setInterval(() => {
      if (Date.now() - startTime.current < TIMEOUT_MS) {
        void refetch();
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refetch]);

  return (
    <div
      className="product-page"
      style={{ textAlign: "center", paddingTop: "64px" }}
    >
      <h1 className="page-title">
        {websiteId ? "Processing your subscription…" : "Subscription confirmed"}
      </h1>
      {!websiteId ? (
        <div className="loading-text" style={{ marginTop: "24px" }}>
          Redirecting to your dashboard…
        </div>
      ) : timedOut ? (
        <div className="alert alert--warning" style={{ marginTop: "24px" }}>
          This is taking longer than expected. Your subscription may still be
          activating in the background — you can{" "}
          <button
            className="btn btn--link"
            onClick={() => {
              startTime.current = Date.now();
              setTimedOut(false);
              void refetch();
            }}
          >
            try again
          </button>{" "}
          or{" "}
          <a href="mailto:support@accessimonitor.app">contact support</a> if the
          issue persists.
        </div>
      ) : (
        <div className="loading-text" style={{ marginTop: "24px" }}>
          Confirming your plan selection, please wait…
        </div>
      )}
    </div>
  );
}
